import React, { memo, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import Modal from "@/components/Modal";

import CommentsModal from "@/modals/CommentsModal";
import CsFormModal from "@/modals/CsFormModal";
import CsListModal from "@/modals/CsListModal";

import { COMMENT_MENU_TYPE } from "@/stores/DeliveryComments";
import {
  useAuthStore,
  useDeliveriesStore,
  useModalStore,
  useProfileStore,
} from "@/stores/hooks";

import { formatDate } from "@/lib/date";

const Title = styled.h4`
  margin: 0;
`;

const ButtonWrap = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;
const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
`;
const ModalButton = styled(Button)``;

const Dl = styled.dl`
  display: flex;
  align-items: flex-start;
  border-bottom: 1px solid #ddd;
  padding-top: 10px;
  padding-bottom: 10px;
  margin-bottom: 0;
`;
const Dt = styled.dt`
  width: 140px;
  line-height: 1;
`;
const Dd = styled.dd`
  margin: 0;
  line-height: 1;
  flex: 1;
  word-break: break-all;
`;

const ImagesWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Image = styled.img`
  width: 100%;
`;

const StatusLogsWrap = styled.ul`
  margin: 0;
  padding-left: 15px;
`;

const StatusLog = styled.li``;

const DeliveryStatusLogs = memo(({ bookId, delivery }) => {
  const { statusLogs } = useDeliveriesStore().state;
  const { ...actions } = useDeliveriesStore();

  const fetchStatusLogsById = useCallback(async () => {
    try {
      await actions.fetchStatusLogsById(bookId);
    } catch (e) {
      // console.log(e);
    }
  }, [actions.fetchStatusLogsById, bookId]);

  useEffect(() => {
    fetchStatusLogsById(bookId);
  }, []);

  return (
    <StatusLogsWrap>
      {statusLogs.map((log, index) => {
        return (
          <StatusLog key={index}>
            {log.renderCreatedAt()} {log.beforeString} → {log.afterString}{" "}
            {delivery.typePickupReturn && log.isDelivery
              ? "(반품/픽업건 입고 진행 중)"
              : ""}
          </StatusLog>
        );
      })}
    </StatusLogsWrap>
  );
});

const DeliveryCollectedLogs = memo(({ collectedLogs }) => {
  return (
    <StatusLogsWrap>
      {collectedLogs.map((log, index) => {
        return (
          <StatusLog key={index}>
            {log.renderCreatedAt()} {log.renderCollectionType()}
          </StatusLog>
        );
      })}
    </StatusLogsWrap>
  );
});

const DeliveryCompletedImage = memo(({ images }) => {
  const [showImages, setShowImages] = useState(false);

  const handleClickShowImages = useCallback(() => {
    setShowImages(true);
  }, []);

  return (
    <ImagesWrap>
      {images.length > 0 && (
        <div>
          <Image src={images[images.length - 1]} alt="배송 완료 이미지" />
        </div>
      )}
      {showImages && (
        <div>
          {images.slice(0, images.length - 1).map((image) => (
            <Image key={image} src={image} alt="배송 완료 이미지" />
          ))}
        </div>
      )}
      {!showImages && images.length > 1 && (
        <Button type="button" onClick={handleClickShowImages}>
          이전 완료 이미지
        </Button>
      )}
    </ImagesWrap>
  );
});

export default function DeliveryModal({ bookId, onAfterSubmit }) {
  const { delivery, sentBackLogs, collectedLogs, delayedDeliveries } =
    useDeliveriesStore().state;
  const { ...actions } = useDeliveriesStore();

  const { closeModal, openModal } = useModalStore();
  const { user: authUser } = useAuthStore().state;
  const { user: profileUser } = useProfileStore().state;
  const canAccessCsCommunication = Boolean(
    authUser?.canAccessCsCommunication || profileUser?.canAccessCsCommunication,
  );

  const [loaded, setLoaded] = useState(false);

  const history = useHistory();

  useEffect(() => {
    fetchByIdOnTheFly(bookId);
    fetchDelayedDeliveriesById(bookId);
    fetchCollectedLogsById(bookId);
    fetchSentBackLogsById(bookId);
  }, []);

  const fetchByIdOnTheFly = useCallback(async () => {
    try {
      await actions.fetchByIdOnTheFly(bookId);
      setLoaded(true);
    } catch (e) {
      window.alert(`배송 조회에 실패하였습니다.\n${e.message}`);
    }
  }, [actions.fetchByIdOnTheFly]);

  const fetchDelayedDeliveriesById = useCallback(async () => {
    try {
      await actions.fetchDelayedDeliveriesById(bookId);
    } catch (e) {
      console.log(e);
    }
  }, [actions.fetchDelayedDeliveriesById]);

  const fetchCollectedLogsById = useCallback(async () => {
    try {
      await actions.fetchCollectedLogsById(bookId);
    } catch (e) {
      console.log(e);
    }
  }, [actions.fetchCollectedLogsById, bookId]);

  const fetchSentBackLogsById = useCallback(async () => {
    try {
      await actions.fetchSentBackLogsById(bookId);
    } catch (e) {
      console.log(e);
    }
  }, [actions.fetchSentBackLogsById]);

  const cancelDelivery = useCallback(async () => {
    if (
      window.confirm(
        `예약번호 ${bookId}건을 취소처리하시겠습니까? 취소처리하시면 해당 건은 더 이상 배송진행을 하실 수 없습니다.`,
      )
    ) {
      try {
        await actions.cancel(bookId);

        onAfterSubmit();
        closeModal();
      } catch (e) {
        window.alert("취소 처리에 실패했습니다. 다시 시도해주세요.");
      }
    }
  }, [actions.cancel, bookId]);

  const returnDelivery = useCallback(() => {
    closeModal();
    history.push(`/deliveries-new?type=return&bookId=${bookId}`);
  }, [history, bookId]);

  const handleOpenComments = useCallback(() => {
    openModal(
      <CommentsModal
        key={`delivery-detail-comments-${bookId}`}
        bookId={bookId}
        menuType={COMMENT_MENU_TYPE.GENERAL}
      />,
    );
  }, [bookId, openModal]);

  const handleOpenCsList = useCallback(() => {
    openModal(
      <CsListModal
        key={`delivery-detail-cs-list-${bookId}`}
        bookId={bookId}
        delivery={delivery}
      />,
    );
  }, [bookId, delivery, openModal]);

  const handleOpenCs = useCallback(() => {
    openModal(
      <CsFormModal
        key={`delivery-detail-cs-${bookId}`}
        bookId={bookId}
        delivery={delivery}
      />,
    );
  }, [bookId, delivery, openModal]);

  const collectionLogWithCs = collectedLogs?.find((d) => !!d.etc?.withCs);
  // const collectionLogWithoutCs = collectedLogs?.find((d) => !d.etc?.withCs);

  return (
    loaded && (
      <Modal size="lg" backdrop={true}>
        <Modal.Header closeButton={true}>
          <Title>배송 정보</Title>
        </Modal.Header>
        <Modal.Body>
          <Dl>
            <Dt>예약번호 (배송상태)</Dt>
            <Dd>
              {delivery.bookId} ({delivery.renderStatus()})
            </Dd>
          </Dl>
          <Dl>
            <Dt>사측 주문번호</Dt>
            <Dd>{delivery.orderIdFromCorp}</Dd>
          </Dl>
          <Dl>
            <Dt>사측 주문번호2</Dt>
            <Dd>{delivery.orderIdFromCorpSub}</Dd>
          </Dl>
          {/* <Dl>
            <Dt>예약일시</Dt>
            <Dd>{delivery.renderBookedAt()}</Dd>
          </Dl> */}
          <Dl>
            <Dt>접수일시</Dt>
            <Dd>{delivery.renderReceiptDate()}</Dd>
          </Dl>

          {delivery.isPossibleToShowDeliveryRider && (
            <Dl>
              <Dt>배송기사 연락처</Dt>
              <Dd>
                {delivery.deliveryRider
                  ? `${delivery.deliveryRiderName} 기사님(${delivery.deliveryRiderMobile})`
                  : ""}
              </Dd>
            </Dl>
          )}
          <Dl>
            <Dt>기업회원</Dt>
            <Dd>{delivery.corpUserCorpTitle}</Dd>
          </Dl>
          <Dl>
            <Dt>기업회원 연락처</Dt>
            <Dd>{delivery.corpUserContact}</Dd>
          </Dl>
          <Dl>
            <Dt>송하인명</Dt>
            <Dd>{delivery.senderName}</Dd>
          </Dl>
          <Dl>
            <Dt>송하인 연락처</Dt>
            <Dd>{delivery.senderMobile}</Dd>
          </Dl>
          <Dl>
            <Dt>송하인 연락처2</Dt>
            <Dd>{delivery.senderMobile2}</Dd>
          </Dl>
          <Dl>
            <Dt>송하인 주소</Dt>
            <Dd>
              {delivery.senderAddressRoad}
              <br />
              {delivery.senderAddress} {delivery.senderAddressDetail}
            </Dd>
          </Dl>
          <Dl>
            <Dt>수하인명</Dt>
            <Dd>{delivery.receiverName}</Dd>
          </Dl>
          <Dl>
            <Dt>수하인 연락처</Dt>
            <Dd>{delivery.receiverMobile}</Dd>
          </Dl>
          <Dl>
            <Dt>수하인 연락처 2</Dt>
            <Dd>{delivery.receiverMobile2}</Dd>
          </Dl>
          <Dl>
            <Dt>수하인 주소</Dt>
            <Dd>
              {delivery.receiverAddressRoad}
              <br />
              {delivery.receiverAddress} {delivery.receiverAddressDetail}
            </Dd>
          </Dl>
          <Dl>
            <Dt>도착동</Dt>
            <Dd>{delivery.receiverDongName}</Dd>
          </Dl>
          <Dl>
            <Dt>물품명</Dt>
            <Dd>{delivery.totalProductName}</Dd>
          </Dl>
          <Dl>
            <Dt>물품가격</Dt>
            <Dd>{delivery.productPrice}</Dd>
          </Dl>
          <Dl>
            <Dt>고객요청사항</Dt>
            <Dd>{delivery.memoFromCustomer}</Dd>
          </Dl>
          <Dl>
            <Dt>기타사항1</Dt>
            <Dd>{delivery.etc1}</Dd>
          </Dl>
          <Dl>
            <Dt>기타사항2</Dt>
            <Dd>{delivery.etc2}</Dd>
          </Dl>
          <Dl>
            <Dt>기타사항3</Dt>
            <Dd>{delivery.etc3}</Dd>
          </Dl>
          <Dl>
            <Dt>타택배사 운송장번호</Dt>
            <Dd>{delivery.agencyOrderId}</Dd>
          </Dl>
          <Dl>
            <Dt>반송일시및사유</Dt>
            <Dd>
              {sentBackLogs?.length > 0
                ? `${sentBackLogs[0].renderCreatedAt()} ${
                    sentBackLogs[0].reason
                  }`
                : ""}
            </Dd>
          </Dl>
          <Dl>
            <Dt>분실일시및사유</Dt>
            <Dd>
              {delivery.renderLostDate()} {delivery.lostReason}
            </Dd>
          </Dl>
          <Dl>
            <Dt>수량값</Dt>
            <Dd>{delivery.totalQuantity}</Dd>
          </Dl>
          <Dl>
            <Dt>
              수취시 서명 이미지
              {delivery.signImageLocations.length > 1 ? `(정정배송)` : ""}
            </Dt>
            <Dd>
              <DeliveryCompletedImage images={delivery.signImageLocations} />
            </Dd>
          </Dl>
          <Dl>
            <Dt>
              미수취 촬영 이미지
              {delivery.notReceivedImageLocations.length > 1
                ? `(정정배송)`
                : ""}
            </Dt>
            <Dd>
              <DeliveryCompletedImage
                images={delivery.notReceivedImageLocations}
              />
            </Dd>
          </Dl>
          <Dl>
            <Dt>파손 촬영 이미지</Dt>
            <Dd>
              <DeliveryCompletedImage
                images={delivery.lostBreakageImageLocations}
              />
            </Dd>
          </Dl>
          <Dl>
            <Dt>반품수거 촬영 이미지</Dt>
            <Dd>
              {delivery.pickupCompletedImage && (
                <Image
                  src={delivery.pickupCompletedImage}
                  alt="반품수거 촬영 이미지"
                />
              )}
            </Dd>
          </Dl>
          <Dl>
            <Dt>최근 미배송</Dt>
            <Dd>
              {delayedDeliveries?.length > 0
                ? `${formatDate(
                    new Date(delayedDeliveries[0].createdAt),
                    "yyyy-MM-dd HH:mm",
                  )} ${delayedDeliveries[0].reason}`
                : delivery.renderDelayReasonCode()}
            </Dd>
          </Dl>
          <Dl>
            <Dt>배송연기 기록</Dt>
            <Dd>{delivery.renderPostponedDate()}</Dd>
          </Dl>

          <Dl>
            <Dt>취소사유</Dt>
            <Dd>{delivery.canceledReason}</Dd>
          </Dl>

          {collectionLogWithCs && (
            <Dl>
              <Dt>CS회수촬영 이미지</Dt>
              <Dd>
                <Image
                  src={collectionLogWithCs?.imagePath}
                  alt="CS회수촬영 이미지"
                />
              </Dd>
            </Dl>
          )}

          {delivery.isCanceled && delivery.pickupCanceledImage && (
            <Dl>
              <Dt>반품수거취소 촬영 이미지</Dt>
              <Dd>
                <Image
                  src={delivery.pickupCanceledImage}
                  alt="반품수거취소 촬영 이미지"
                />
              </Dd>
            </Dl>
          )}

          {delivery.isCanceled &&
            delivery.pickupCanceledLatitude &&
            delivery.pickupCanceledLongitude && (
              <Dl>
                <Dt>반품수거취소 위치(위경도)</Dt>
                <Dd>
                  {delivery.pickupCanceledLatitude},{" "}
                  {delivery.pickupCanceledLongitude}
                </Dd>
              </Dl>
            )}

          <Dl>
            <Dt>배송로그</Dt>
            <Dd>
              <DeliveryStatusLogs bookId={bookId} delivery={delivery} />
            </Dd>
          </Dl>
          <Dl>
            <Dt>회수로그</Dt>
            <Dd>
              <DeliveryCollectedLogs collectedLogs={collectedLogs} />
            </Dd>
          </Dl>
          <ButtonWrap>
            <ButtonGroup>
              {canAccessCsCommunication && (
                <>
                  <ModalButton
                    onClick={handleOpenComments}
                    type="button"
                    width="80px"
                  >
                    코멘트
                  </ModalButton>
                  <ModalButton
                    onClick={handleOpenCsList}
                    type="button"
                    width="80px"
                  >
                    CS보기
                  </ModalButton>
                  <ModalButton
                    onClick={handleOpenCs}
                    type="button"
                    width="80px"
                  >
                    CS등록
                  </ModalButton>
                </>
              )}
            </ButtonGroup>
            <ButtonGroup>
              {delivery.isPossibleToCancel && (
                <ModalButton
                  onClick={cancelDelivery}
                  type="button"
                  width="120px"
                >
                  취소 신청
                </ModalButton>
              )}

              {delivery.isPossibleToReturn && (
                <ModalButton
                  onClick={returnDelivery}
                  type="button"
                  width="120px"
                >
                  반품 신청
                </ModalButton>
              )}

              <ModalButton
                onClick={closeModal}
                type="button"
                variant="outline-secondary"
                width="120px"
              >
                닫기
              </ModalButton>
            </ButtonGroup>
          </ButtonWrap>
        </Modal.Body>
      </Modal>
    )
  );
}
